import React, { useEffect, useState } from 'react';
import { Search, TrendingUp, Sparkles } from 'lucide-react';
import { PromptCard, PromptListItem } from '../components/PromptCard';
import { PromptPaywall, isPromptyUnlocked } from '../components/PromptPaywall';
import { usePageMetadata } from '../hooks/usePageMetadata';

const CATEGORIES: Array<{ value: string; label: string }> = [
    { value: '', label: 'Wszystkie' },
    { value: 'ecommerce', label: 'E-commerce' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'obsluga-klienta', label: 'Obsługa klienta' },
    { value: 'seo', label: 'SEO' },
    { value: 'social', label: 'Social media' },
    { value: 'automatyzacje', label: 'Automatyzacje' },
    { value: 'reklamy', label: 'Reklamy' },
];

const TOOLS: Array<{ value: string; label: string }> = [
    { value: '', label: 'Każde narzędzie' },
    { value: 'chatgpt', label: 'ChatGPT' },
    { value: 'claude', label: 'Claude' },
    { value: 'midjourney', label: 'Midjourney' },
    { value: 'sora', label: 'Sora' },
    { value: 'gemini', label: 'Gemini' },
];

export const PromptyPage: React.FC = () => {
    const [items, setItems] = useState<PromptListItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [category, setCategory] = useState('');
    const [tool, setTool] = useState('');
    const [order, setOrder] = useState<'recent' | 'top'>('recent');
    const [unlocked, setUnlocked] = useState(false);
    const [showInlinePaywall, setShowInlinePaywall] = useState(false);

    useEffect(() => {
        setUnlocked(isPromptyUnlocked());
    }, []);

    useEffect(() => {
        const params = new URLSearchParams();
        if (category) params.set('category', category);
        if (tool) params.set('tool', tool);
        if (order) params.set('order', order);
        setLoading(true);
        fetch(`/api/prompty?${params.toString()}`)
            .then(r => r.json())
            .then(d => setItems(d.items || []))
            .catch(err => { console.error('[prompty]', err); setItems([]); })
            .finally(() => setLoading(false));
    }, [category, tool, order]);

    const filtered = search
        ? items.filter(i =>
            i.title.toLowerCase().includes(search.toLowerCase()) ||
            (i.caption_pl || '').toLowerCase().includes(search.toLowerCase())
        )
        : items;

    const freeCount = items.filter(i => i.is_premium === 0).length;
    const premiumCount = items.filter(i => i.is_premium === 1).length;

    usePageMetadata({
        title: 'Biblioteka 50+ promptów AI dla MŚP i e-commerce | BiznesBezKlikania.pl',
        description: 'Gotowe prompty ChatGPT, Claude i Midjourney dla małych firm. Opisy produktów, reklamy, obsługa klienta. Codziennie nowe. Zacznij darmowo.',
        ogData: { type: 'website' },
    });

    return (
        <>
            {/* Hero */}
            <section className="bg-gradient-to-br from-blue-50 via-white to-violet-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 py-16 sm:py-24">
                <div className="container mx-auto px-4 text-center max-w-4xl">
                    <div className="inline-flex items-center gap-2 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-4 py-1.5 rounded-full text-sm font-medium mb-6">
                        <Sparkles className="w-4 h-4" /> Codziennie nowe prompty
                    </div>
                    <h1 className="text-4xl sm:text-6xl font-bold mb-6 leading-tight">
                        Najlepsze prompty AI<br/>
                        <span className="bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-transparent">
                            dla Twojej firmy
                        </span>
                    </h1>
                    <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
                        Selekcja viralowych promptów ChatGPT, Claude i Midjourney — przerobiona pod polskich przedsiębiorców. Opisy produktów, reklamy, obsługa klienta, content. Kopiujesz i wklejasz.
                    </p>
                    <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-600 dark:text-gray-400">
                        <span><b className="text-emerald-600 dark:text-emerald-400">{freeCount}</b> darmowych</span>
                        <span><b className="text-amber-600 dark:text-amber-400">{premiumCount}</b> premium (1 email = wszystkie)</span>
                        <span>🇵🇱 Po polsku</span>
                    </div>
                </div>
            </section>

            {/* Filtry */}
            <section className="border-b border-gray-200 dark:border-gray-800 sticky top-[64px] bg-white/95 dark:bg-gray-950/95 backdrop-blur z-30">
                <div className="container mx-auto px-4 py-4">
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="relative flex-grow min-w-[200px] max-w-md">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <input
                                type="text"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                placeholder="Szukaj promptu…"
                                className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-full bg-white dark:bg-gray-900 focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        <select
                            value={category}
                            onChange={e => setCategory(e.target.value)}
                            className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-full bg-white dark:bg-gray-900 focus:ring-2 focus:ring-blue-500"
                        >
                            {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                        </select>
                        <select
                            value={tool}
                            onChange={e => setTool(e.target.value)}
                            className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-full bg-white dark:bg-gray-900 focus:ring-2 focus:ring-blue-500"
                        >
                            {TOOLS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                        <div className="flex items-center gap-1 ml-auto">
                            <button
                                onClick={() => setOrder('recent')}
                                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${order === 'recent' ? 'bg-blue-600 text-white' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
                            >Najnowsze</button>
                            <button
                                onClick={() => setOrder('top')}
                                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors flex items-center gap-1 ${order === 'top' ? 'bg-blue-600 text-white' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
                            >
                                <TrendingUp className="w-3 h-3" /> Najpopularniejsze
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Lista */}
            <section className="container mx-auto px-4 py-12">
                {loading ? (
                    <div className="text-center py-20 text-gray-500">Wczytuję prompty…</div>
                ) : filtered.length === 0 ? (
                    <div className="text-center py-20">
                        <p className="text-gray-500 dark:text-gray-400 mb-4">Brak promptów spełniających kryteria.</p>
                        <button
                            onClick={() => { setSearch(''); setCategory(''); setTool(''); }}
                            className="text-blue-600 dark:text-blue-400 hover:underline"
                        >Wyczyść filtry</button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {filtered.map(p => <PromptCard key={p.id} prompt={p} unlocked={unlocked} />)}
                    </div>
                )}
            </section>

            {/* CTA inline paywall jeśli nieodblokowany */}
            {!unlocked && (
                <section className="container mx-auto px-4 pb-16">
                    {showInlinePaywall ? (
                        <PromptPaywall
                            variant="inline"
                            onUnlocked={() => { setUnlocked(true); setShowInlinePaywall(false); }}
                        />
                    ) : (
                        <div className="bg-gradient-to-br from-blue-600 to-violet-600 rounded-3xl p-8 sm:p-12 text-white text-center">
                            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                                Odblokuj wszystkie {premiumCount} premium promptów
                            </h2>
                            <p className="text-lg text-blue-50 mb-6 max-w-2xl mx-auto">
                                Jeden email — dostęp do całej biblioteki + nowy prompt każdego dnia w skrzynce.
                            </p>
                            <button
                                onClick={() => setShowInlinePaywall(true)}
                                className="bg-white text-blue-700 font-bold px-8 py-3 rounded-full hover:opacity-90 transition-opacity"
                            >
                                Odblokuj za darmo →
                            </button>
                        </div>
                    )}
                </section>
            )}
        </>
    );
};
